import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import useFeatureFlags, { FeatureFlags } from '../../../hooks/useFeatureFlags'
import WipAiSummaryActions from './WipAiSummaryActions'
import WipEasyMdeField from './WipEasyMdeField'

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function applyInlineMarkdown(escaped) {
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
}

function inlineMd(raw) {
  return applyInlineMarkdown(escapeHtml(raw))
}

function isMarkdownTableRow(line) {
  const t = String(line).trim()
  return t.includes('|') && t.replace(/\|/g, '').trim() !== ''
}

function isMarkdownTableSeparator(line) {
  const t = String(line)
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
  if (!t.includes('-')) return false
  const cells = t.split('|')
  return cells.length > 0 && cells.every(cell => /^\s*:?-{3,}:?\s*$/.test(cell))
}

function splitMarkdownTableCells(line) {
  let t = String(line).trim()
  if (t.startsWith('|')) t = t.slice(1)
  if (t.endsWith('|')) t = t.slice(0, -1)
  return t.split('|').map(cell => inlineMd(cell.trim()))
}

function consumeMarkdownTable(lines, start) {
  if (
    start + 1 >= lines.length ||
    !isMarkdownTableRow(lines[start]) ||
    !isMarkdownTableSeparator(lines[start + 1])
  ) {
    return null
  }

  const headers = splitMarkdownTableCells(lines[start])
  let i = start + 2
  const rows = []
  while (i < lines.length && isMarkdownTableRow(lines[i]) && !isMarkdownTableSeparator(lines[i])) {
    rows.push(splitMarkdownTableCells(lines[i]))
    i += 1
  }

  const thead = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`
  const tbody = `<tbody>${rows
    .map(row => `<tr>${row.map(c => `<td>${c}</td>`).join('')}</tr>`)
    .join('')}</tbody>`
  return { html: `<table>${thead}${tbody}</table>`, next: i }
}

export function markdownSummaryToHtml(markdown) {
  if (!markdown || !String(markdown).trim()) return ''

  const blocks = String(markdown)
    .split(/\n\n+/)
    .map(b => b.trim())
    .filter(Boolean)
  const parts = []

  for (const block of blocks) {
    const lines = block.split('\n')
    const firstLine = lines[0].trim()
    const headingMatch = firstLine.match(/^(#{1,6})\s+(.*)$/)

    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 6)
      parts.push(`<h${level}>${inlineMd(headingMatch[2])}</h${level}>`)
      const tail = lines
        .slice(1)
        .join('\n')
        .trim()
      if (tail) {
        parts.push(blockToHtmlParagraphsAndLists(tail))
      }
      continue
    }

    parts.push(blockToHtmlParagraphsAndLists(block))
  }

  return parts.join('')
}

function blockToHtmlParagraphsAndLists(text) {
  const lines = text.split('\n')
  const out = []
  let i = 0

  while (i < lines.length) {
    const trimmed = lines[i].trim()
    if (!trimmed) {
      i += 1
      continue
    }

    const table = consumeMarkdownTable(lines, i)
    if (table) {
      out.push(table.html)
      i = table.next
      continue
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/)
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 6)
      out.push(`<h${level}>${inlineMd(headingMatch[2])}</h${level}>`)
      i += 1
      continue
    }

    const bullet = trimmed.match(/^[-*]\s+(.+)$/)
    const ordered = trimmed.match(/^\d+\.\s+(.+)$/)

    if (bullet || ordered) {
      const isOl = Boolean(ordered)
      const items = []
      while (i < lines.length) {
        const t = lines[i].trim()
        if (!t) break
        const b = t.match(/^[-*]\s+(.+)$/)
        const o = t.match(/^\d+\.\s+(.+)$/)
        const m = isOl ? o : b
        if (!m) break
        items.push(`<li>${inlineMd(m[1])}</li>`)
        i += 1
      }
      out.push(isOl ? `<ol>${items.join('')}</ol>` : `<ul>${items.join('')}</ul>`)
      continue
    }

    const paraLines = []
    while (i < lines.length && lines[i].trim()) {
      const t = lines[i].trim()
      if (/^[-*]\s/.test(t) || /^\d+\.\s/.test(t) || /^#{1,6}\s+/.test(t)) break
      if (i + 1 < lines.length && isMarkdownTableRow(t) && isMarkdownTableSeparator(lines[i + 1])) {
        break
      }
      paraLines.push(lines[i])
      i += 1
    }
    const para = paraLines.join('\n').trim()
    if (para) {
      const withBreaks = inlineMd(para).replace(/\n/g, '<br />')
      out.push(`<p>${withBreaks}</p>`)
    }
  }

  return out.join('')
}

const useStyles = makeStyles(theme => ({
  editorWrap: {
    width: '100%',
    /* ProseMirror (Tiptap) content styles */
    '& .ProseMirror': {
      outline: 'none',
    },
    '& .ProseMirror h1, & .ProseMirror h2, & .ProseMirror h3, & .ProseMirror h4': {
      marginTop: '0.6em',
      marginBottom: '0.3em',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    '& .ProseMirror h1': { fontSize: '1.25rem' },
    '& .ProseMirror h2': { fontSize: '1.15rem' },
    '& .ProseMirror h3': { fontSize: '1.05rem' },
    '& .ProseMirror p': { margin: '0 0 0.6em' },
    '& .ProseMirror p:last-child': { marginBottom: 0 },
    '& .ProseMirror ul, & .ProseMirror ol': {
      paddingLeft: '1.25rem',
      margin: '0.4em 0',
    },
    '& .ProseMirror li': { margin: '0.2em 0' },
    '& .ProseMirror strong': { fontWeight: 600 },
    /* Placeholder text */
    '& .ProseMirror p.is-editor-empty:first-child::before': {
      content: 'attr(data-placeholder)',
      color: '#aaa',
      pointerEvents: 'none',
      float: 'left',
      height: 0,
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}))

const WipOrationEditor = props => {
  const classes = useStyles()
  const {
    client,
    projectId,
    month,
    value,
    onChange,
    onInternalChange,
    onInternalLoadingChange,
    onGenerated,
  } = props
  const { isEnabled } = useFeatureFlags()
  const aiEnabled = isEnabled(FeatureFlags.ENABLE_WIP_REPORT_AI) === true

  const editorOptions = useMemo(() => ({ placeholder: 'Type your summary here…' }), [])

  return (
    <div style={{ padding: 20, width: '100%', margin: 'auto' }}>
      <div className={classes.headerRow}>
        <h2 style={{ margin: 0 }}>Summary</h2>
        {aiEnabled && (
          <WipAiSummaryActions
            client={client}
            projectId={projectId}
            month={month}
            value={value}
            onChange={onChange}
            onInternalChange={onInternalChange}
            onInternalLoadingChange={onInternalLoadingChange}
            onGenerated={onGenerated}
          />
        )}
      </div>
      <div className={classes.editorWrap} data-testid="wip-oration-editor">
        <WipEasyMdeField value={value} onChange={onChange} options={editorOptions} />
      </div>
    </div>
  )
}

WipOrationEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  client: PropTypes.object,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  month: PropTypes.string,
  onInternalChange: PropTypes.func,
  onInternalLoadingChange: PropTypes.func,
  onGenerated: PropTypes.func,
}

WipOrationEditor.defaultProps = {
  value: '',
  client: null,
  projectId: null,
  month: null,
  onInternalChange: null,
  onInternalLoadingChange: null,
  onGenerated: null,
}

export default WipOrationEditor
